<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="case-form-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.back()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <h1 class="page-title">{{ $t('case.createCase') }}</h1>

      <!-- Pre-fill notice -->
      <div v-if="fromCalculator" class="prefill-notice">
        {{ $t('case.fromCalculator') }}
      </div>

      <form @submit.prevent="handleSubmit" class="case-form">
        <!-- Section: Deceased Details -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('case.deceased') }}</h3>
          <div class="form-grid">
            <div class="form-field span-2">
              <label>{{ $t('case.deceasedName') }} *</label>
              <IntercomInput
                v-model="formData.deceased_name"
                :placeholder="$t('case.deceasedName')"
                required
              />
            </div>
            <div class="form-field">
              <label>{{ $t('case.deceasedGender') }} *</label>
              <select v-model="formData.deceased_gender" class="select-input" required>
                <option value="male">{{ $t('calculator.male') }}</option>
                <option value="female">{{ $t('calculator.female') }}</option>
              </select>
            </div>
            <div class="form-field">
              <IntercomDateInput
                v-model="formData.date_of_death"
                :label="$t('case.dateOfDeath')"
              />
            </div>
            <div class="form-field">
              <label>{{ $t('case.school') }} *</label>
              <select v-model="formData.school" class="select-input" required>
                <option value="hanafi">{{ $t('calculator.hanafi') }}</option>
                <option value="shia_ithna_ashari">{{ $t('calculator.shiaIthnaAshari') }}</option>
              </select>
            </div>
            <div class="form-field">
              <label>{{ $t('case.language') }}</label>
              <select v-model="formData.language" class="select-input">
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="ur">Urdu</option>
                <option value="ta">Tamil</option>
              </select>
            </div>
          </div>
        </IntercomCard>

        <!-- Section: Heirs -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('case.heirs') }}</h3>
          <HeirInput
            :available-heirs="availableHeirs"
            :heir-counts="heirCounts"
            @increment="incrementHeir"
            @decrement="decrementHeir"
            @toggle="toggleHeir"
          />
        </IntercomCard>

        <!-- Section: Estate Details -->
        <IntercomCard class="form-section">
          <EstateForm
            :estate-value="estateValue"
            :funeral-expenses="funeralExpenses"
            :debts="debts"
            :unpaid-mahr="unpaidMahr"
            :wasiyyah-amount="wasiyyahAmount"
            @update:estate-value="estateValue = $event"
            @update:funeral-expenses="funeralExpenses = $event"
            @update:debts="debts = $event"
            @update:unpaid-mahr="unpaidMahr = $event"
            @update:wasiyyah-amount="wasiyyahAmount = $event"
          />
          <div class="form-field" style="margin-top: var(--space-4)">
            <label>{{ $t('case.wasiyyahRecipient') }}</label>
            <IntercomInput
              v-model="formData.wasiyyah_recipient"
              :placeholder="$t('case.wasiyyahRecipient')"
            />
          </div>
        </IntercomCard>

        <!-- Section: Contact & Notes -->
        <IntercomCard class="form-section">
          <h3 class="section-title">Contact & Notes</h3>
          <div class="form-grid">
            <div class="form-field">
              <label>{{ $t('case.contactName') }}</label>
              <IntercomInput
                v-model="formData.contact_name"
                :placeholder="$t('case.contactName')"
              />
            </div>
            <div class="form-field">
              <label>{{ $t('case.contactPhone') }}</label>
              <IntercomInput
                v-model="formData.contact_phone"
                placeholder="10-digit phone"
                maxlength="10"
                inputmode="numeric"
              />
              <span v-if="phoneError" class="field-error">{{ phoneError }}</span>
            </div>
            <div class="form-field span-2">
              <label>{{ $t('case.notes') }}</label>
              <textarea
                v-model="formData.notes"
                :placeholder="$t('case.notesPlaceholder')"
                class="textarea-input"
                rows="4"
              ></textarea>
            </div>
          </div>
        </IntercomCard>

        <!-- Form Actions -->
        <div class="form-actions">
          <IntercomButton variant="secondary" type="button" @click="$router.back()">
            {{ $t('common.cancel') }}
          </IntercomButton>
          <IntercomButton
            variant="primary"
            type="submit"
            :loading="isSubmitting"
            :disabled="!hasHeirs"
          >
            {{ $t('common.submit') }}
          </IntercomButton>
        </div>
      </form>

      <!-- Error -->
      <div v-if="errorMessage" class="form-error">
        {{ errorMessage }}
      </div>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useCalculator } from '../composables/useCalculator.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomInput,
  IntercomDateInput,
} from '@shared/components/intercom'
import { validatePhone } from '@shared/utils/validators.js'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import HeirInput from '../components/HeirInput.vue'
import EstateForm from '../components/EstateForm.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { createCase } = useFaraid()
const { navSections } = useNavigation()
const {
  deceasedGender,
  school,
  estateValue,
  funeralExpenses,
  debts,
  unpaidMahr,
  wasiyyahAmount,
  heirCounts,
  availableHeirs,
  heirsPayload,
  hasHeirs,
  incrementHeir,
  decrementHeir,
  toggleHeir,
  loadFromSession,
  clearSession,
} = useCalculator()

const formData = reactive({
  deceased_name: '',
  deceased_gender: 'male',
  date_of_death: '',
  school: 'hanafi',
  language: 'en',
  contact_name: '',
  contact_phone: '',
  notes: '',
  wasiyyah_recipient: '',
})

const isSubmitting = ref(false)
const errorMessage = ref('')
const phoneError = ref('')
const fromCalculator = ref(false)

// Strip non-digit characters from phone as user types
watch(() => formData.contact_phone, (val) => {
  const digits = val.replace(/\D/g, '')
  if (digits !== val) formData.contact_phone = digits
  if (phoneError.value && (!digits || validatePhone(digits))) phoneError.value = ''
})

// Sync formData with calculator state
const syncFromCalculator = () => {
  formData.deceased_gender = deceasedGender.value
  formData.school = school.value
}

const handleSubmit = async () => {
  try {
    errorMessage.value = ''

    if (!formData.deceased_name.trim()) {
      errorMessage.value = t('error.invalidForm')
      return
    }

    if (!hasHeirs.value) {
      errorMessage.value = t('calculator.noHeirsSelected')
      return
    }

    if (formData.contact_phone.trim() && !validatePhone(formData.contact_phone.trim())) {
      phoneError.value = t('error.invalidPhone', 'Enter a valid 10-digit phone number')
      return
    }

    isSubmitting.value = true

    const payload = {
      deceased_name: formData.deceased_name.trim(),
      deceased_gender: formData.deceased_gender,
      school: formData.school,
      heirs: heirsPayload.value,
      language: formData.language,
      source: 'website',
    }

    if (formData.date_of_death) payload.date_of_death = formData.date_of_death
    if (estateValue.value) payload.estate_value = Number(estateValue.value)
    if (funeralExpenses.value) payload.funeral_expenses = Number(funeralExpenses.value)
    if (debts.value) payload.debts = Number(debts.value)
    if (unpaidMahr.value) payload.unpaid_mahr = Number(unpaidMahr.value)
    if (wasiyyahAmount.value) payload.wasiyyah_amount = Number(wasiyyahAmount.value)
    if (formData.wasiyyah_recipient.trim()) payload.wasiyyah_recipient = formData.wasiyyah_recipient.trim()
    if (formData.contact_name.trim()) payload.contact_name = formData.contact_name.trim()
    if (formData.contact_phone.trim()) payload.contact_phone = formData.contact_phone.trim()
    if (formData.notes.trim()) payload.notes = formData.notes.trim()

    const response = await createCase(payload)
    const caseData = response.case || response
    clearSession()
    toast.success(t('success.caseCreated'))
    router.push(`/cases/${caseData.id}`)
  } catch (error) {
    errorMessage.value = error.message || t('error.saveFailed')
  } finally {
    isSubmitting.value = false
  }
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()

  // Pre-fill from calculator if coming from /calculator
  if (route.query.fromCalc === 'true') {
    const loaded = loadFromSession()
    if (loaded) {
      fromCalculator.value = true
      syncFromCalculator()
    }
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.case-form-view {
  padding: var(--space-8);
  max-width: 900px;
  margin: 0 auto;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: var(--space-4) 0 var(--space-6) 0;
}

.prefill-notice {
  padding: var(--space-3) var(--space-4);
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-radius: var(--radius-md);
  color: #1e40af;
  font-size: var(--font-size-sm);
  margin-bottom: var(--space-6);
}

.case-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-section {
  padding: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.form-field.span-2 {
  grid-column: span 2;
}

.form-field label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.select-input {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-white);
}

.select-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.textarea-input {
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-family: inherit;
  resize: vertical;
}

.textarea-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-4);
}

.field-error {
  color: var(--color-error);
  font-size: var(--font-size-xs);
  margin-top: var(--space-1);
}

.form-error {
  margin-top: var(--space-4);
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
}

@media (max-width: 768px) {
  .case-form-view {
    padding: var(--space-4);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field.span-2 {
    grid-column: span 1;
  }
}
</style>
